import { createSlice } from '@reduxjs/toolkit';
import { clearUserLogged, logoutAction, setSessionExpired, switchIdpAccount } from 'features/common/commonSlice';
import {
  followExpedientAsync,
  getExpedientFollowAsync,
  getFollowedSummaryAsync,
  markExpedientSeenAsync,
  unfollowExpedientAsync,
} from './asyncActions';
import { ExpedientFollowState, FollowedSummary } from './constants';

export interface ExpedientFollowSliceState {
  summary: FollowedSummary;
  /** Estado de seguimiento por expediente abierto (botón del diálogo, la ficha y la atención). */
  byExpedient: Record<string, ExpedientFollowState>;
  /** Expedientes con un seguir / dejar de seguir en vuelo: el botón se deshabilita. */
  pending: Record<string, boolean>;
}

const initialState: ExpedientFollowSliceState = {
  summary: { total: 0, unread: 0, items: [] },
  byExpedient: {},
  pending: {},
};

const markRead = (summary: FollowedSummary, expedientId: string) => {
  const item = summary.items.find((it) => it.expedientId === expedientId);
  if (item && item.unread) {
    item.unread = false;
    item.lastSeenTmst = Date.now();
    summary.unread = Math.max(0, summary.unread - 1);
  }
};

export const expedientFollowSlice = createSlice({
  name: 'expedientFollow',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(getFollowedSummaryAsync.fulfilled, (state, action) => {
        state.summary = action.payload;
      })
      .addCase(getExpedientFollowAsync.fulfilled, (state, action) => {
        state.byExpedient[action.payload.expedientId] = action.payload.state;
      })
      // Seguir / dejar de seguir: el botón cambia en cuanto se pulsa; la respuesta trae la lista real de
      // seguidores y, si falla, se vuelve a lo que había (el toast lo pone el middleware).
      .addCase(followExpedientAsync.pending, (state, action) => {
        const id = action.meta.arg;
        state.pending[id] = true;
        state.byExpedient[id] = { followers: state.byExpedient[id]?.followers ?? [], following: true };
      })
      .addCase(followExpedientAsync.fulfilled, (state, action) => {
        delete state.pending[action.payload.expedientId];
        state.byExpedient[action.payload.expedientId] = action.payload.state;
      })
      .addCase(followExpedientAsync.rejected, (state, action) => {
        const id = action.meta.arg;
        delete state.pending[id];
        if (state.byExpedient[id]) state.byExpedient[id].following = false;
      })
      .addCase(unfollowExpedientAsync.pending, (state, action) => {
        const id = action.meta.arg;
        state.pending[id] = true;
        state.byExpedient[id] = { followers: state.byExpedient[id]?.followers ?? [], following: false };
      })
      .addCase(unfollowExpedientAsync.fulfilled, (state, action) => {
        delete state.pending[action.payload.expedientId];
        state.byExpedient[action.payload.expedientId] = action.payload.state;
      })
      .addCase(unfollowExpedientAsync.rejected, (state, action) => {
        const id = action.meta.arg;
        delete state.pending[id];
        if (state.byExpedient[id]) state.byExpedient[id].following = true;
      })
      // Visto: el punto y el contador bajan sin esperar a la red; el siguiente resumen corrige si falló.
      .addCase(markExpedientSeenAsync.pending, (state, action) => {
        markRead(state.summary, action.meta.arg);
      })
      // El logout no recarga la página: sin esto el siguiente usuario vería los seguidos del anterior.
      .addCase(logoutAction, () => initialState)
      .addCase(clearUserLogged, () => initialState)
      .addCase(setSessionExpired, () => initialState)
      .addCase(switchIdpAccount, () => initialState);
  },
});

export default expedientFollowSlice.reducer;
